/**
 * SLA (Service Level Agreement) Types for CRM
 *
 * NOTE: These are app-specific types for SLA management.
 * There are no corresponding library types in @open/nexus-public-api.
 *
 * This module provides:
 * - SLA policy definitions (response/resolution times)
 * - Escalation rules with automatic queue routing
 * - SLA timer states and status tracking
 * - Compliance metrics
 */

/**
 * SLA Priority Levels
 */
export type SlaPriority = 'low' | 'mid' | 'high' | 'critical'

/**
 * SLA Status for visual indicators
 */
export type SlaStatus = 'on-track' | 'warning' | 'breached'

/**
 * SLA Escalation Rule
 * Defines what happens when SLA time limits are exceeded
 */
export interface SlaEscalation {
  id: string
  level: number // Escalation level (1, 2, 3...)
  triggerMinutes: number // Minutes after creation when this escalation triggers
  targetQueueId: string // Queue ID to move interaction to
  notifyRoles?: string[] // Roles to notify (supervisor, manager, director, etc.)
  description?: string
}

/**
 * SLA Policy
 * Defines service level agreement rules for a category of interactions
 */
export interface CrmSlaPolicy {
  id: string
  name: string
  description?: string
  parentTagId: string // Parent tag category (e.g., 'actions', 'complaints-verbal')
  childTagId?: string // Optional child tag for more specific matching
  priority: SlaPriority
  firstResponseTime: number // Minutes for first response
  resolutionTime: number // Minutes for resolution
  escalations: SlaEscalation[]
  isActive: boolean
  createdAt: string
  updatedAt: string
}

/**
 * SLA Timer State
 * Real-time state of SLA for an interaction
 */
export interface SlaTimerState {
  policyId: string
  policyName: string
  status: SlaStatus
  timeRemainingSeconds: number // Negative value = breached
  totalTimeSeconds: number
  percentRemaining: number // 0-100
  color: 'blue' | 'yellow' | 'red' | 'green'
  escalationLevel: number // Current escalation level (0 = none)
  nextEscalation?: {
    level: number
    triggerAt: Date
    targetQueueId: string
  }
}

/**
 * Create SLA Policy Parameters
 */
export interface CrmSlaCreateParams {
  name: string
  description?: string
  parentTagId: string
  childTagId?: string
  priority: SlaPriority
  firstResponseTime: number
  resolutionTime: number
  escalations?: SlaEscalation[]
}

/**
 * Update SLA Policy Parameters
 */
export interface CrmSlaUpdateParams {
  id: string
  name?: string
  description?: string
  parentTagId?: string
  childTagId?: string
  priority?: SlaPriority
  firstResponseTime?: number
  resolutionTime?: number
  escalations?: SlaEscalation[]
  isActive?: boolean
}

/**
 * SLA Metrics
 * Statistics about SLA compliance
 */
export interface SlaMetrics {
  totalInteractions: number
  onTrack: number
  warning: number
  breached: number
  complianceRate: number // Percentage (0-100)
  averageResponseTime: number // Minutes
  averageResolutionTime: number // Minutes
}

/**
 * SLA Event
 * Events triggered by SLA system
 */
export type SlaEventType =
  | 'response_time_warning'
  | 'response_time_breached'
  | 'resolution_time_warning'
  | 'resolution_time_breached'
  | 'escalation_triggered'

export interface SlaEvent {
  type: SlaEventType
  interactionId: string
  policyId: string
  timestamp: Date
  escalationLevel?: number
  targetQueueId?: string
}

/**
 * Escalation Action Types
 */
export enum EscalationAction {
  NOTIFY = 'notify',
  REASSIGN = 'reassign',
  ESCALATE = 'escalate',
}

/**
 * Escalation Role Types
 */
export enum EscalationRole {
  SUPERVISOR = 'supervisor',
  DEPARTMENT_HEAD = 'department_head',
  DIRECTOR = 'director',
  ADMIN = 'admin',
}

/**
 * Escalation Rule for SLA
 */
export interface EscalationRule {
  id: string
  name: string
  afterHours: number
  notifyRole: EscalationRole
  notifyUserIds: string[]
  action: EscalationAction
  autoReassign?: boolean
  reassignToUserId?: string
  reassignToQueueId?: string
  isActive: boolean
}

/**
 * Default SLA Policies (based on requirements)
 */
export const DEFAULT_SLA_POLICIES: Omit<CrmSlaPolicy, 'id' | 'createdAt' | 'updatedAt'>[] = [
  {
    name: 'Standard Actions',
    description: 'Processing standard action requests',
    parentTagId: 'actions',
    priority: 'mid',
    firstResponseTime: 60,
    resolutionTime: 24 * 60,
    escalations: [
      {
        id: 'esc-actions-1',
        level: 1,
        triggerMinutes: 72 * 60,
        targetQueueId: 'escalation-l1',
        notifyRoles: ['supervisor'],
        description: 'Escalation to team supervisor',
      },
      {
        id: 'esc-actions-2',
        level: 2,
        triggerMinutes: 84 * 60,
        targetQueueId: 'escalation-l2',
        notifyRoles: ['manager'],
        description: 'Escalation to department manager',
      },
    ],
    isActive: true,
  },
]
